import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  // FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  // Radio,
  // RadioGroup,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import axios from "axios";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import http from "../../utils/http";
import { errorToastMessage } from "../../utils/toast";
import {
  AuthLayout,
  //  AuthRadioSelector,
  SignInWrapper,
} from "./auth.style";
import { StyledButton } from "../Common/styles/button";
import ForceLogoutModal from "./ForceLogoutModal";
import { useAppDispatch } from "../../Redux/hooks";
import { setUserAuth } from "../../Redux/reducers/userSlice";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import isEmail from "validator/es/lib/isEmail";
import { Logo } from "../Common/assets/Sidebar";
import { useQuery } from "../../utils/hooks";
import { clearExplicitLogout, safeRedirect } from "../../utils/Route";

type LoginType = "email" | "login_id";

const schema = yup.object({
  loginType: yup.string(),
  email: yup
    .string()
    .trim()
    .when("loginType", {
      is: "login_id",
      then: (s) => s.required("*Login ID is Required"),
      otherwise: (s) =>
        s
          .required("*Email ID is Required")
          .test("is-valid", "*Please enter a valid Email ID.", (value) =>
            value ? isEmail(value) : false
          ),
    }),
  password: yup
    .string()
    .min(6, "*Password too short")
    .required("*Password is Required"),
});

const SignIn = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const emailverified = query.get("email_invite");
  const redirectTo = safeRedirect(query.get("redirect"));
  const [submitLoader, setSubmitLoader] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [logoutData, setLogoutData] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const initialLoginType: LoginType = redirectTo?.includes("/interview")
    ? "login_id"
    : "email";

  const dispatch = useAppDispatch();

  useEffect(() => {
    clearExplicitLogout();
  }, []);

  const closeModal = () => {
    setShowModal(false);
  };

  const openModal = (data: any) => {
    setLogoutData(data);
    setShowModal(true);
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (data: any) => {
    try {
      setSubmitLoader(true);
      const body: any = {
        username: data.email.trim(),
        password: data.password,
        userAgent: window.navigator.userAgent,
        strategy: data.loginType,
      };
      let url = "user/auth/login";
      const res = await http.post(url, body);
      setSubmitLoader(true);
      if (res?.data?.data?.isMfaEnabled) {
        sessionStorage.setItem("otp-token", res?.data?.data?.token);
        if (redirectTo) {
          sessionStorage.setItem("post-login-redirect", redirectTo);
        }
        navigate("/auth/2fa");
      } else {
        const token = res.data.data.accessToken;
        const refreshToken = res.data.data.refreshToken;
        const userId = res.data.data.id;
        const firstName = res?.data?.data?.firstName;
        const lastName = res?.data?.data?.lastName;
        localStorage.setItem("sm-access-token", token);
        localStorage.setItem("sm-refresh-token", refreshToken);
        localStorage.setItem("user-id", userId);
        localStorage.setItem("first-name", firstName);
        localStorage.setItem("last-name", lastName);
        dispatch(setUserAuth({ authenticated: true, role: "", userId }));
        if (redirectTo) {
          navigate(redirectTo);
        }
      }
    } catch (err) {
      setSubmitLoader(false);
      if (axios.isAxiosError(err) && err?.response?.status === 409) {
        openModal(data);
      } else {
        errorToastMessage(err as Error);
      }
    }
  };

  return (
    <Box sx={AuthLayout}>
      <Box sx={SignInWrapper}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}>
          <Logo />
        </Box>
        {emailverified &&
          (emailverified === "success" ? (
            <Alert
              severity="success"
              sx={{
                mb: 2,
              }}
            >
              You have successfully accepted the invitation. Please sign in to
              continue.
            </Alert>
          ) : emailverified === "failed" ? (
            <Alert
              severity="error"
              sx={{
                mb: 2,
              }}
            >
              The Invitation link is not valid anymore. Please contact
              administrator to get a new invitation.
            </Alert>
          ) : null)}
        <Formik
          initialValues={{
            loginType: initialLoginType,
            email: "",
            password: "",
          }}
          onSubmit={handleSubmit}
          validationSchema={schema}
        >
          {({
            errors,
            touched,
            getFieldProps,
            values,
            setFieldValue,
            setFieldTouched,
          }) => {
            const isLoginId = values.loginType === "login_id";
            return (
              <Form>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  color="primary"
                  value={values.loginType}
                  onChange={(_, value) => {
                    if (!value) return;
                    setFieldValue("loginType", value);
                    setFieldTouched("email", false);
                  }}
                  sx={{ mb: 3 }}
                >
                  <ToggleButton value="email">Email</ToggleButton>
                  <ToggleButton value="login_id">Login ID</ToggleButton>
                </ToggleButtonGroup>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="email">
                    {isLoginId ? "Your Login ID" : "Your Email"}
                  </FormLabel>
                  <TextField
                    placeholder={
                      isLoginId ? "e.g. acme-int-01" : "name@example.com"
                    }
                    autoComplete="username"
                    id="email"
                    {...getFieldProps("email")}
                    error={touched?.email && errors?.email ? true : false}
                    helperText={
                      touched?.email && errors?.email ? errors?.email : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="password">
                    Password
                  </FormLabel>
                  <TextField
                    placeholder="Your password"
                    id="password"
                    {...getFieldProps("password")}
                    error={touched?.password && errors?.password ? true : false}
                    helperText={
                      touched?.password && errors?.password
                        ? errors?.password
                        : " "
                    }
                    type={showPassword ? "text" : "password"}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
                {!submitLoader ? (
                  <StyledButton variant="contained" type="submit" fullWidth>
                    Sign In
                  </StyledButton>
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={25} />
                  </Box>
                )}
                <Box sx={{ textAlign: "center", mt: 2 }}>
                  {isLoginId ? (
                    <Typography variant="body2" color="text.secondary">
                      Forgot your password? Ask your study administrator to
                      regenerate it.
                    </Typography>
                  ) : (
                    <NavLink to="/auth/forgot-password">
                      Forgot password
                    </NavLink>
                  )}
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>
      {showModal && (
        <ForceLogoutModal
          showModal={showModal}
          closeModal={closeModal}
          logoutData={logoutData}
        />
      )}
    </Box>
  );
};

export default SignIn;
